import React, { createContext, useContext, useState, useCallback } from 'react';
import i18n from '../i18n/config';
import { localizeApiMessage } from '../utils/fetchErrorMessage';
import Notification from '../components/shared/Notification';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    isVisible: false,
    type: 'info',
    message: '',
    duration: 5000
  });

  const showNotification = useCallback((type, message, duration = 5000) => {
    const localized =
      typeof message === 'string' ? localizeApiMessage(message, (key) => i18n.t(key)) : message;
    setNotification({
      isVisible: true,
      type,
      message: localized,
      duration
    });

    // Auto hide after duration
    setTimeout(() => {
      hideNotification();
    }, duration);
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  // Convenience methods
  const showInfo = useCallback((message, duration) => {
    showNotification('info', message, duration);
  }, [showNotification]);

  const showSuccess = useCallback((message, duration) => {
    showNotification('success', message, duration);
  }, [showNotification]);

  const showError = useCallback((message, duration) => {
    showNotification('error', message, duration);
  }, [showNotification]);

  const showWarning = useCallback((message, duration) => {
    showNotification('warning', message, duration);
  }, [showNotification]);

  return (
    <NotificationContext.Provider value={{
      notification,
      showNotification,
      hideNotification,
      showInfo,
      showSuccess,
      showError,
      showWarning
    }}>
      {children}
      <Notification
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </NotificationContext.Provider>
  );
};
